import { useCallback, useEffect, useState } from 'react';
import { subscriptionGateway } from './index';
import { planAccessOf, resolveEntitlementState, type EntitlementDisplayState, type FeatureSummary, type PlanAccess } from './types';

export type FeatureAccess =
  | { status: 'loading' }
  /** The state could not be determined. Never guessed: callers leave the control available and let the backend
   * (the real boundary) answer, instead of showing a padlock for what may be an infrastructure fault. */
  | { status: 'unknown' }
  | { status: 'resolved'; display: EntitlementDisplayState; feature: FeatureSummary; access: PlanAccess };

/**
 * Resolves, from the real backend, whether the company's plan currently entitles it to `featureKey`, for a
 * screen to render the matching state. Display convenience only: the protected endpoint enforces entitlement
 * itself, so an `unknown` or stale result here can never let a restricted action through.
 *
 * `refresh()` re-reads the current state and returns it — used after a protected action is refused (403) to
 * tell a plan/subscription refusal (shown as the restriction notice) from a missing role permission (shown as
 * a permission message), instead of guessing from the status code alone.
 */
export function useFeatureAccess(featureKey: string): { featureAccess: FeatureAccess; refresh: () => Promise<FeatureAccess> } {
  const [featureAccess, setFeatureAccess] = useState<FeatureAccess>({ status: 'loading' });

  const resolve = useCallback(async (signal?: AbortSignal): Promise<FeatureAccess> => {
    try {
      const [features, subscription] = await Promise.all([
        subscriptionGateway.listFeatures(signal),
        subscriptionGateway.getCurrentSubscription(signal),
      ]);
      const feature = features.find((f) => f.featureKey === featureKey);
      if (!feature) return { status: 'unknown' };
      const access = planAccessOf(subscription);
      return { status: 'resolved', display: resolveEntitlementState(feature, access), feature, access };
    } catch {
      return { status: 'unknown' };
    }
  }, [featureKey]);

  useEffect(() => {
    const controller = new AbortController();
    void resolve(controller.signal).then((result) => {
      if (!controller.signal.aborted) setFeatureAccess(result);
    });
    return () => controller.abort();
  }, [resolve]);

  const refresh = useCallback(async () => {
    const result = await resolve();
    setFeatureAccess(result);
    return result;
  }, [resolve]);

  return { featureAccess, refresh };
}
